<script setup lang="ts">
import { computed, ref, nextTick } from 'vue';
import { ChevronRight, ChevronDown, Trash2 } from 'lucide-vue-next';
import { VisuallyHidden } from 'radix-vue';
import cloneDeep from 'lodash-es/cloneDeep';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { StudyPeriodCategory } from '@/types';
import ExpandCollapseTransition from '@/components/common/ExpandCollapseTransition.vue';

interface Props {
    categories: StudyPeriodCategory[];
    errorMessage: string | null;
}

const props = defineProps<Props>();

const selectedComparisonType = defineModel<'within' | 'between'>('selectedComparisonType', { required: true });
const selectedCategoryOptions = defineModel<number[]>('selectedCategoryOptions', { required: true });

const isCategoryPickerOpen = ref(false);

const comparisonOptions = computed(() => [
    {
        label: i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.multipleTeams.label'),
        description: i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.multipleTeams.description'),
        value: 'between',
    },
    {
        label: i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.singleTeam.label'),
        description: i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.comparisonType.singleTeam.description'),
        value: 'within',
    },
]);

const categoryOptions = computed(
    () =>
        props.categories.map((category) => {
            const options = category.options.map((option) => ({
                ...option,
                checked: selectedCategoryOptions.value.includes(option.id),
            }));

            const checkedOptions = options.filter((option) => option.checked);

            return {
                ...category,
                options,
                checked: checkedOptions.length === options.length,
                indeterminate: checkedOptions.length > 0 && checkedOptions.length < options.length,
            };
        }) ?? []
);
const selectedCategoryIndex = ref(0);
const activeCategory = computed(() => categoryOptions.value[selectedCategoryIndex.value]);

function selectActiveCategory(index: number) {
    selectedCategoryIndex.value = index;
}

function toggleCategory(category: any) {
    if (category.checked) {
        const optionIdsToRemove = category.options.filter((o: any) => o.checked).map((o: any) => o.id);

        selectedCategoryOptions.value = selectedCategoryOptions.value.filter((option) => !optionIdsToRemove.includes(option));
    } else {
        const newSelectedCategoryOptions = new Set([...selectedCategoryOptions.value, ...category.options.map((o: any) => o.id)]);

        selectedCategoryOptions.value = [...newSelectedCategoryOptions];
    }
}

function toggleCategoryOption(option: any) {
    if (selectedCategoryOptions.value.includes(option.id)) {
        selectedCategoryOptions.value = selectedCategoryOptions.value.filter((o) => o !== option.id);
    } else {
        selectedCategoryOptions.value = [...selectedCategoryOptions.value, option.id];
    }
}

const selectedChartCategoriesCount = computed(
    () => selectedCategoryOptions.value.filter((optionId) => props.categories.some((c) => c.options.some((option) => option.id === optionId && option.type === 'chart'))).length
);
const selectedTableCategoriesCount = computed(
    () => selectedCategoryOptions.value.filter((optionId) => props.categories.some((c) => c.options.some((option) => option.id === optionId && option.type === 'table'))).length
);

const displayValue = computed(() => {
    const texts = [];

    if (selectedChartCategoriesCount.value > 0) {
        texts.push(
            `${selectedChartCategoriesCount.value} ${
                selectedChartCategoriesCount.value === 1
                    ? i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.chart')
                    : i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.charts')
            }`
        );
    }

    if (selectedTableCategoriesCount.value > 0) {
        texts.push(
            `${selectedTableCategoriesCount.value} ${
                selectedTableCategoriesCount.value === 1
                    ? i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.table')
                    : i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.tables')
            }`
        );
    }

    return texts.length ? texts.join(', ') : i18n.global.t('views.monitoring.teamComparison.fields.categoryPicker.texts.noSelection');
});

interface ManageOption {
    id: number;
    name: string;
    categoryName: string;
}

const selectedOptionsToManage = computed<ManageOption[]>(() => {
    const options: ManageOption[] = [];

    for (const category of categoryOptions.value) {
        for (const option of category.options) {
            if (selectedCategoryOptions.value.includes(option.id)) {
                options.push({
                    id: option.id,
                    name: option.name,
                    categoryName: category.name,
                });
            }
        }
    }

    return options;
});

const showManageDialog = ref(false);

const optionsToManage = ref<ManageOption[]>([]);

async function openManageDialog() {
    optionsToManage.value = cloneDeep(selectedOptionsToManage.value);
    await nextTick();
    showManageDialog.value = true;
}

function removeManageOption(optionId: number) {
    optionsToManage.value = optionsToManage.value.filter((o) => o.id !== optionId);
}

function saveManageOptions() {
    selectedCategoryOptions.value = optionsToManage.value.map((o) => o.id);
    showManageDialog.value = false;
}
</script>

<template>
    <Popover v-model:open="isCategoryPickerOpen">
        <PopoverTrigger as-child>
            <button class="mt-3 w-full min-h-[46px] flex items-center justify-between gap-x-3 bg-white border rounded-md py-2.5 px-3.5 text-left transition-colors" :class="{ 'border-red-500': errorMessage }">
                <div class="flex items-center gap-x-1 leading-normal">
                    <span class="font-medium">{{ $t('views.monitoring.teamComparison.fields.categoryPicker.label') }}:</span>
                    <span class="text-gray-500">{{ displayValue }}</span>
                </div>
                <ChevronDown class="flex-shrink-0 h-5 w-5 transition-transform" :class="{ 'rotate-180': isCategoryPickerOpen }" aria-hidden="true" />
            </button>
        </PopoverTrigger>
        <PopoverContent trigger-width align="start" class="flex flex-col p-0 h-[516px] overflow-hidden">
            <div class="flex justify-between gap-x-8 px-4 py-5">
                <div>
                    <RadioGroup v-model="selectedComparisonType" class="gap-x-5">
                        <div v-for="option in comparisonOptions" :key="option.value" class="flex items-start cursor-pointer">
                            <RadioGroupItem :id="`comparison-type-option-id-${option.value}`" :value="option.value.toString()" />
                            <Label :for="`comparison-type-option-id-${option.value}`" class="font-normal cursor-pointer pl-2 -mt-[2.5px]">
                                <div class="flex flex-col">
                                    <span class="text-base font-medium">{{ option.label }}</span>
                                    <span class="text-sm text-gray-500 max-w-sm">{{ option.description }}</span>
                                </div>
                            </Label>
                        </div>
                    </RadioGroup>
                </div>
                <div class="flex justify-end">
                    <Button variant="secondary" size="sm" :disabled="!selectedOptionsToManage.length" @click="openManageDialog">
                        {{ $t('views.monitoring.teamComparison.fields.categoryPicker.buttons.manageSelected') }}
                        <div v-if="selectedOptionsToManage.length > 0" class="ml-1 text-sm flex-shrink-0 font-medium flex items-center justify-center p-1 rounded-full border border-primary-800 min-w-5 h-5">
                            {{ selectedOptionsToManage.length }}
                        </div>
                    </Button>
                </div>
            </div>
            <div role="tree" aria-orientation="horizontal" class="grid grid-cols-2 divide-x h-full max-h-full overflow-hidden border-t">
                <div class="flex flex-col items-stretch min-h-0 max-h-full" role="group">
                    <div class="sticky z-10 top-0 bg-white font-medium text-base border-b px-4 py-2">{{ $t('views.monitoring.teamComparison.fields.categoryPicker.groups.category') }}</div>
                    <div class="max-h-full overflow-y-auto">
                        <div
                            v-for="(category, categoryIndex) in categoryOptions"
                            :key="category.id"
                            class="flex items-center cursor-pointer"
                            role="treeitem"
                            :aria-label="category.name"
                            aria-level="1"
                            :aria-expanded="selectedCategoryIndex === categoryIndex"
                            :aria-setsize="categoryOptions.length"
                            :aria-posinset="categoryIndex + 1"
                            tabindex="0"
                            @keydown.enter="selectActiveCategory(categoryIndex)"
                            @mouseover="selectActiveCategory(categoryIndex)"
                            @click="selectActiveCategory(categoryIndex)"
                        >
                            <label
                                class="flex gap-x-3 items-center px-3.5 py-3.5 w-full cursor-pointer transition-colors"
                                :class="{
                                    'hover:bg-gray-100/70 focus:bg-gray-100/70': selectedCategoryIndex !== categoryIndex,
                                    'bg-primary-300 bg-opacity-30': selectedCategoryIndex === categoryIndex,
                                }"
                            >
                                <CustomCheckbox :checked="category.checked" :indeterminate="category.indeterminate" disable-transition class="flex-shrink-0" @update:checked="toggleCategory(category)" />
                                <div class="flex grow gap-x-3 items-center justify-between max-w-full min-w-0">
                                    <span class="grow select-none truncate">{{ category.name }}</span>
                                    <ChevronRight class="flex-shrink-0 h-4 w-4 -mr-1" />
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="flex flex-col items-stretch min-h-0 max-h-full" role="group">
                    <div class="sticky z-10 top-0 bg-white font-medium text-base border-b px-4 py-2">{{ $t('views.monitoring.teamComparison.fields.categoryPicker.groups.selectView') }}</div>
                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-0"
                        leave-to-class="opacity-0"
                        mode="out-in"
                    >
                        <div v-if="activeCategory" class="max-h-full overflow-y-auto">
                            <div
                                v-for="(option, optionIndex) in activeCategory.options"
                                :key="option.id"
                                role="treeitem"
                                :aria-label="option.name"
                                aria-level="2"
                                :aria-setsize="activeCategory.options.length"
                                :aria-posinset="optionIndex + 1"
                                class="flex items-center cursor-pointer"
                                tabindex="0"
                            >
                                <label class="flex gap-x-3 items-center px-3.5 py-3.5 w-full cursor-pointer hover:bg-gray-100/70 focus:bg-gray-100/70 transition-colors">
                                    <CustomCheckbox :checked="option.checked" disable-transition class="flex-shrink-0" @update:checked="toggleCategoryOption(option)" />
                                    <div class="grow flex items-center justify-between gap-x-2 select-none max-w-full min-w-0">
                                        <div class="flex items-center gap-x-1.5 min-w-0">
                                            <div class="whitespace-nowrap truncate min-w-0">{{ option.name }}</div>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
            <div class="py-3 px-4 border-t">
                <ExpandCollapseTransition>
                    <div v-if="errorMessage" class="text-red-500 text-right font-medium">
                        <div class="pb-4">
                            {{ errorMessage }}
                        </div>
                    </div>
                </ExpandCollapseTransition>
                <div class="flex justify-end">
                    <Button class="w-44" :disabled="!!errorMessage" @click="isCategoryPickerOpen = false">{{ $t('views.monitoring.teamComparison.fields.categoryPicker.buttons.saveSelected') }}</Button>
                </div>
            </div>
            <Dialog v-model="showManageDialog" class="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>
                        {{ $t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.title') }}
                    </DialogTitle>
                    <VisuallyHidden>
                        <DialogDescription>
                            {{ $t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.description') }}
                        </DialogDescription>
                    </VisuallyHidden>
                </DialogHeader>
                <div class="pt-2">
                    <table class="w-full">
                        <thead>
                            <tr>
                                <th class="text-left font-medium text-gray-400 pl-4 pr-2 pt-2 pb-4">
                                    {{ $t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.table.headers.category') }}
                                </th>
                                <th class="text-left font-medium text-gray-400 pl-4 pr-2 pt-2 pb-4">
                                    {{ $t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.table.headers.options') }}
                                </th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody class="border border-gray-100">
                            <tr v-for="option in optionsToManage" :key="option.id" class="odd:bg-gray-100">
                                <td class="pl-4 pr-2 py-2">
                                    {{ option.categoryName }}
                                </td>
                                <td class="pl-4 pr-2 py-2">
                                    {{ option.name }}
                                </td>
                                <td class="text-right pl-4 pr-2 py-2">
                                    <Button variant="ghost" size="icon" class="text-red-500 hover:text-red-600 hover:bg-red-100" @click="removeManageOption(option.id)">
                                        <Trash2 class="w-4 h-4" />
                                    </Button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <DialogFooter class="flex sm:justify-center pt-2">
                    <DialogClose as-child>
                        <Button variant="ghost" size="lg" class="min-w-44">{{ $t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.buttons.cancel') }}</Button>
                    </DialogClose>
                    <Button size="lg" class="min-w-44" @click="saveManageOptions">{{ $t('views.monitoring.teamComparison.fields.categoryPicker.dialogs.manageSelected.buttons.save') }}</Button>
                </DialogFooter>
            </Dialog>
        </PopoverContent>
    </Popover>
</template>
